import { CPopover } from "@coreui/react";

import {
  CREDIT_NOTE_STATUS_APPROVED_BY_SET,
  CREDIT_NOTE_STATUS_CANCELLATION_FAILED,
  CREDIT_NOTE_STATUS_CANCELLATION_REQUESTED,
  CREDIT_NOTE_STATUS_CANCELLED,
  CREDIT_NOTE_STATUS_CHOICES,
  CREDIT_NOTE_STATUS_ERROR,
  CREDIT_NOTE_STATUS_RECOVERABLE_ERROR,
  CREDIT_NOTE_STATUS_RESEND_REQUESTED,
  CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
  CREDIT_NOTE_STATUS_UPLOADED_TO_SET,
  CreditNote,
} from "../../models/credit-note";
import { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import DocumentError from "../../models/document-error";
import { getItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";

interface CreditNoteCrudStatusProps {
  creditNote: CreditNote;
}

const CreditNoteCrudStatus: React.FC<CreditNoteCrudStatusProps> = ({
  creditNote,
}) => {
  const [currentCreditNote, setCurrentCreditNote] = useState(creditNote);
  const [retrievedError, setRetrievedError] = useState<DocumentError>();
  const [loadingError, setLoadingError] = useState(false);

  useEffect(() => {
    setCurrentCreditNote(creditNote);
  }, [creditNote]);

  useSub(`credit_note_${creditNote.id}`, (creditNote: CreditNote) => {
    setCurrentCreditNote(creditNote);
  });

  useEffect(() => {
    const fetchError = async () => {
      const errorCode = currentCreditNote.setResponseCode;
      const errorMessage =
        currentCreditNote.setResponseDescription || currentCreditNote.setError;

      if (
        !(
          currentCreditNote.status === CREDIT_NOTE_STATUS_ERROR ||
          currentCreditNote.status === CREDIT_NOTE_STATUS_CANCELLATION_FAILED ||
          currentCreditNote.status === CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR
        )
      ) {
        setRetrievedError(undefined);
        return;
      }

      if (!errorCode && !errorMessage) {
        setRetrievedError(undefined);
        return;
      }

      setLoadingError(true);
      try {
        let result;
        if (errorCode) {
          const params = new Map<string, string | undefined>();
          params.set("error_code", errorCode);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (
          (!result || result.status !== SUCCESS || !result.data) &&
          errorMessage
        ) {
          const params = new Map<string, string | undefined>();
          params.set("raw_message", errorMessage);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (result?.status === SUCCESS && result.data) {
          setRetrievedError(result.data);
        } else {
          setRetrievedError(undefined);
        }
      } catch (err) {
        console.error("Error loading document error:", err);
        setRetrievedError(undefined);
      } finally {
        setLoadingError(false);
      }
    };

    fetchError();
  }, [currentCreditNote]);

  let badgeClassName = "badge-info";

  if (currentCreditNote.status === CREDIT_NOTE_STATUS_UPLOADED_TO_SET) {
    badgeClassName = "badge-warning";
  }

  if (
    currentCreditNote.status === CREDIT_NOTE_STATUS_ERROR ||
    creditNote.status === CREDIT_NOTE_STATUS_RECOVERABLE_ERROR
  ) {
    badgeClassName = "badge-danger";
  }

  if (currentCreditNote.status === CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR) {
    badgeClassName = "badge-danger";
  }

  if (currentCreditNote.status === CREDIT_NOTE_STATUS_APPROVED_BY_SET) {
    badgeClassName = "badge-success";
  }

  if (currentCreditNote.status === CREDIT_NOTE_STATUS_CANCELLATION_REQUESTED) {
    badgeClassName = "badge-light";
  }

  if (currentCreditNote.status === CREDIT_NOTE_STATUS_CANCELLED) {
    badgeClassName = "badge-info";
  }

  if (currentCreditNote.status === CREDIT_NOTE_STATUS_CANCELLATION_FAILED) {
    badgeClassName = "badge-danger";
  }

  if (currentCreditNote.status === CREDIT_NOTE_STATUS_RESEND_REQUESTED) {
    badgeClassName = "badge-warning";
  }

  const renderPopoverContent = () => {
    if (
      ![
        CREDIT_NOTE_STATUS_ERROR,
        CREDIT_NOTE_STATUS_CANCELLATION_FAILED,
        CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
      ].includes(currentCreditNote.status!)
    )
      return null;

    return (
      <ul
        style={{
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingBottom: "0px",
          marginBottom: "0px",
        }}
      >
        {currentCreditNote.setResponseCode && (
          <li>Código: {currentCreditNote.setResponseCode}</li>
        )}
        {currentCreditNote.setResponseDescription && (
          <li>Descripción: {currentCreditNote.setResponseDescription}</li>
        )}
        {currentCreditNote.setError && (
          <li>Error: {currentCreditNote.setError}</li>
        )}
        {!loadingError && retrievedError && (
          <>
            {retrievedError.description && (
              <li>Significado: {retrievedError.description}</li>
            )}
            {retrievedError.possibleSolutions && (
              <li>Posible solución: {retrievedError.possibleSolutions}</li>
            )}
          </>
        )}
      </ul>
    );
  };

  return (
    <td className="text-center">
      <span
        className={`badge badge-pill ${badgeClassName}`}
        style={{ fontSize: "14px" }}
      >
        {CREDIT_NOTE_STATUS_CHOICES.get(currentCreditNote.status!)}{" "}
        {renderPopoverContent() ? (
          <CPopover
            content={renderPopoverContent()}
            placement="top"
            trigger="click"
          >
            <span
              className="fa fa-info-circle"
              style={{ cursor: "pointer" }}
            ></span>
          </CPopover>
        ) : null}
      </span>
    </td>
  );
};

export default CreditNoteCrudStatus;
